import {
    BlastRadiusOrg,
    Classifier,
    Organisation,
    OrgSummary,
    Profile,
    ProfileSummary,
    Team,
    WireframeUser
} from './types'

const HATE_PROMPT_V3 = `You are a content-safety classifier for brand social channels.

Classify the COMMENT into one of: SAFE, SUSPECT, SEVERE.

Mark SEVERE when the comment contains hate speech, slurs, or
threats targeting a person or protected group (race, religion, gender).

Return JSON: { "label": ..., "rationale": ... }`

const HATE_PROMPT_V4 = `You are a content-safety classifier for brand social channels.

Classify the COMMENT into one of: SAFE, SUSPECT, SEVERE.

Mark SEVERE when the comment contains hate speech, slurs, or
threats targeting a person or protected group (race, religion, gender,
sexuality, disability).

Mark SUSPECT for hostile or demeaning language that stops short
of the above.

Return JSON: { "label": ..., "rationale": ... }`

export const CLASSIFIERS: Classifier[] = [
    {
        id: 'hate-harassment',
        title: 'Hate & Harassment',
        clientFacingName: 'Abuse & harassment',
        description:
            'Flags abusive, threatening or hateful comments directed at people or protected groups.',
        modality: 'text',
        latestVersion: 4,
        liveVersion: 3,
        profilesUsing: 247,
        teamsUsing: 11,
        versions: [
            { version: 4, author: 'You', date: 'editing now', status: 'draft', prompt: HATE_PROMPT_V4, defaultBorderlineThreshold: 0.45, defaultConfirmedThreshold: 0.65, model: 'gemini-2.5-flash-lite' },
            { version: 3, author: 'S. Okafor', date: '2 days ago', status: 'live', prompt: HATE_PROMPT_V3, defaultBorderlineThreshold: 0.5, defaultConfirmedThreshold: 0.7, model: 'gemini-2.5-flash-lite' },
            { version: 2, author: 'S. Okafor', date: '1 month ago', status: 'archived', prompt: HATE_PROMPT_V3, defaultBorderlineThreshold: 0.5, defaultConfirmedThreshold: 0.7, model: 'gemini-2.5-flash' },
            { version: 1, author: 'D. Cole', date: '3 months ago', status: 'archived', prompt: HATE_PROMPT_V3, defaultBorderlineThreshold: 0.55, defaultConfirmedThreshold: 0.75, model: 'gemini-2.5-flash' }
        ]
    },
    {
        id: 'spam-scams',
        title: 'Spam & Scams',
        clientFacingName: 'Spam & scams',
        description: 'Detects scam links, crypto bait and repetitive promotional spam.',
        modality: 'text',
        latestVersion: 6,
        liveVersion: 6,
        profilesUsing: 301,
        teamsUsing: 12,
        versions: [
            { version: 6, author: 'S. Okafor', date: '3 weeks ago', status: 'live', prompt: 'Detect spam and scams. Return JSON.', defaultBorderlineThreshold: 0.6, defaultConfirmedThreshold: 0.8, model: 'gemini-2.5-flash-lite' }
        ]
    },
    {
        id: 'self-harm',
        title: 'Self-harm & Crisis',
        clientFacingName: 'Self-harm & crisis',
        description:
            'Identifies comments that may indicate self-harm or crisis, for fast escalation.',
        modality: 'text',
        // latestVersion = highest version number across all statuses (draft or live);
        // liveVersion = the version running everywhere. A pending draft above the live
        // version is found via versions.find(v => v.status === 'draft'), not a separate field.
        latestVersion: 3,
        liveVersion: 2,
        profilesUsing: 180,
        teamsUsing: 7,
        versions: [
            { version: 3, author: 'You', date: '1 day ago', status: 'draft', prompt: 'Detect crisis language. Return JSON.', defaultBorderlineThreshold: 0.25, defaultConfirmedThreshold: 0.4, model: 'gemini-2.5-pro' },
            { version: 2, author: 'S. Okafor', date: '5 days ago', status: 'live', prompt: 'Detect crisis language. Return JSON.', defaultBorderlineThreshold: 0.3, defaultConfirmedThreshold: 0.45, model: 'gemini-2.5-flash' }
        ]
    },
    {
        id: 'brand-risk-imagery',
        title: 'Brand Risk — Imagery',
        clientFacingName: 'Unsafe imagery',
        description: 'Reviews attached images for unsafe or off-brand visual content.',
        modality: 'image',
        latestVersion: 1,
        liveVersion: 1,
        profilesUsing: 14,
        teamsUsing: 2,
        versions: [
            { version: 1, author: 'D. Cole', date: '1 month ago', status: 'live', prompt: 'Review image for brand risk.', defaultBorderlineThreshold: 0.4, defaultConfirmedThreshold: 0.6, model: 'gemini-2.5-flash' }
        ]
    },
    {
        id: 'competitor-mentions',
        title: 'Competitor Mentions',
        clientFacingName: 'Competitor mentions',
        description: 'Acme — Social only. Flags comments naming key competitors.',
        modality: 'text',
        custom: true,
        latestVersion: 2,
        liveVersion: 1,
        profilesUsing: 1,
        teamsUsing: 1,
        versions: [
            { version: 2, author: 'You', date: '6 days ago', status: 'draft', prompt: 'Flag competitor names.', defaultBorderlineThreshold: 0.35, defaultConfirmedThreshold: 0.5, model: 'gemini-2.5-flash-lite' },
            { version: 1, author: 'You', date: '2 weeks ago', status: 'live', prompt: 'Flag competitor names.', defaultBorderlineThreshold: 0.35, defaultConfirmedThreshold: 0.5, model: 'gemini-2.5-flash-lite' }
        ]
    }
]

export const ORGANISATION: Organisation = {
    id: 'acme',
    name: 'Acme Corp',
    keyContact: { name: 'Priya Nair', email: 'priya@acme.com' },
    notesMarkdown:
        '**Onboarded:** Jan 2026 · Enterprise\n\n**Watch-outs:** sensitive to false positives on political content — keep Hate classifier conservative.\n\n**Renewal:** Q3. Champion is Priya (Head of Social).',
    teamCount: 3,
    profileCount: 88
}

// All organisations, for the organisations list page. Acme Corp's figures match
// ORGANISATION above; the rest are illustrative. status drives the list's badge.
export const ORGANISATIONS: OrgSummary[] = [
    { id: 'acme', name: 'Acme Corp', keyContact: 'Priya Nair', plan: 'Enterprise', teamCount: 3, profileCount: 88, userCount: 5, status: 'Active' },
    { id: 'globex', name: 'Globex', keyContact: 'Marcus Vaughn', plan: 'Growth', teamCount: 2, profileCount: 64, userCount: 4, status: 'Active' },
    { id: 'initech', name: 'Initech', keyContact: 'Dana Klein', plan: 'Growth', teamCount: 1, profileCount: 22, userCount: 2, status: 'Onboarding' },
    { id: 'soylent', name: 'Soylent Media', keyContact: 'Ravi Desai', plan: 'Enterprise', teamCount: 4, profileCount: 120, userCount: 9, status: 'Active' },
    { id: 'stark', name: 'Stark Industries', keyContact: 'Pepper Potts', plan: 'Enterprise', teamCount: 5, profileCount: 210, userCount: 14, status: 'Active' },
    { id: 'umbrella', name: 'Umbrella Co', keyContact: 'Alex Wong', plan: 'Growth', teamCount: 1, profileCount: 12, userCount: 1, status: 'Suspended' }
]

// note is a coverage summary — classifiers live on profiles, so the interesting
// team-level fact is whether every profile beneath has them.
export const TEAMS: Team[] = [
    { id: 'acme-social', name: 'Acme — Social', profileCount: 42, note: '4 profiles missing Hate & Harassment' },
    { id: 'acme-support', name: 'Acme — Support', profileCount: 31, note: 'all classifiers on all 31 profiles' },
    { id: 'acme-brand', name: 'Acme — Brand', profileCount: 15, note: 'all classifiers on all 15 profiles' }
]

export const PROFILE: Profile = { id: 'acme-uk-yt', name: '@AcmeUK', platform: 'YouTube' }

// Summary metadata for the profile overview / Users tab on ProfileConfigPage.
export const PROFILE_OVERVIEW = {
    platform: 'YouTube',
    teamName: 'Acme — Social',
    createdBy: 'Priya Nair',
    createdOn: '12 Jan 2026',
    status: 'Authorised',
    lastActivity: '2 hours ago',
    totalComments: 48210,
    moderatedComments: 1320
}

// Representative sample of profiles across the org's teams. The headline counts
// (ORGANISATION.profileCount / Team.profileCount) are the real totals; this list
// is the subset shown in the wireframe's profile listings. The first entry is the
// profile rendered by ProfileConfigPage.
export const PROFILES: ProfileSummary[] = [
    { id: 'acme-uk-yt', name: '@AcmeUK', platform: 'YouTube', teamId: 'acme-social', teamName: 'Acme — Social' },
    { id: 'acme-uk-ig', name: '@AcmeUK', platform: 'Instagram', teamId: 'acme-social', teamName: 'Acme — Social' },
    { id: 'acme-x', name: '@Acme', platform: 'X', teamId: 'acme-social', teamName: 'Acme — Social' },
    { id: 'acme-support-x', name: '@AcmeSupport', platform: 'X', teamId: 'acme-support', teamName: 'Acme — Support' },
    { id: 'acme-help-fb', name: 'Acme Help', platform: 'Facebook', teamId: 'acme-support', teamName: 'Acme — Support' },
    { id: 'acme-brand-ig', name: '@AcmeBrand', platform: 'Instagram', teamId: 'acme-brand', teamName: 'Acme — Brand' },
    { id: 'acme-brand-tt', name: '@AcmeBrand', platform: 'TikTok', teamId: 'acme-brand', teamName: 'Acme — Brand' }
]

export const USERS: WireframeUser[] = [
    { id: 'u1', name: 'Priya Nair', email: 'priya@acme.com', role: 'Admin', status: 'Active', lastActive: '2 hours ago', isKeyContact: true, teams: [], allTeams: true },
    { id: 'u2', name: 'Tom Reed', email: 'tom@acme.com', role: 'Member', status: 'Active', lastActive: 'Yesterday', teams: ['Acme — Social'] },
    { id: 'u3', name: 'Jess Okonkwo', email: 'jess@acme.com', role: 'Member', status: 'Active', lastActive: '3 days ago', teams: ['Acme — Support', 'Acme — Brand'] },
    { id: 'u4', name: 'Liam Brooks', email: 'liam@acme.com', role: 'Viewer', status: 'Active', lastActive: '1 week ago', teams: ['Acme — Social'] },
    { id: 'u5', name: 'sara@acme.com', email: 'sara@acme.com', role: 'Member', status: 'Invited', lastActive: '—', inviteNote: 'Invitation sent 2 days ago', teams: ['Acme — Brand'] }
]

const ACME_RADIUS: BlastRadiusOrg[] = [
    {
        name: 'Acme Corp',
        level: 'org',
        teamCount: 3,
        profileCount: 88,
        teams: [
            { name: 'Acme — Social', profileCount: 42 },
            { name: 'Acme — Support', profileCount: 31 },
            { name: 'Acme — Brand', profileCount: 15 }
        ]
    },
    {
        name: 'Globex',
        level: 'org',
        teamCount: 2,
        profileCount: 64,
        teams: [
            { name: 'Globex — Comms', profileCount: 40 },
            { name: 'Globex — Care', profileCount: 24 }
        ]
    },
    { name: 'Initech — Social', level: 'team', teamCount: 1, profileCount: 22, teams: [] }
]

export const getClassifierById = (id: string): Classifier | undefined =>
    CLASSIFIERS.find((c) => c.id === id)

// Returns the org/team/profile nodes a go-live or delete would affect.
// Mock: every classifier shares the same illustrative radius.
export const blastRadiusFor = (_classifierId: string): BlastRadiusOrg[] => ACME_RADIUS
